import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, listResource } from '@diggens/api-client';

export interface PicklistItem {
  id: string;
  value: string;
  label: string;
  description?: string;
  color?: string;
  is_default?: boolean;
  is_closed?: boolean;
  is_won?: boolean;
  order: number;
  sort_order?: number;
  [key: string]: unknown;
}

export interface Picklist {
  id: string;
  name: string;
  items: PicklistItem[];
}

/**
 * Hook to get a single picklist by name
 */
export function usePicklist(name: string) {
  return useQuery({
    queryKey: ['picklist', name],
    queryFn: async () => {
      const { data } = await listResource<Picklist>('picklists', { name });
      return data[0] ?? null;
    },
    enabled: !!name,
  });
}

/**
 * Hook to get all picklists for the current tenant
 */
export function usePicklists(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['picklists'],
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const { data } = await listResource<Picklist>('picklists');
      return data;
    },
  });
}

/**
 * Hook to update picklist items
 */
export function useUpdatePicklistItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, items }: { name: string; items: PicklistItem[] }) =>
      apiFetch<Picklist>(`/resource/picklists/by-name/${encodeURIComponent(name)}/items`, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      }),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['picklist', data.name] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['picklist', variables.name] });
      }
      queryClient.invalidateQueries({ queryKey: ['picklists'] });
    },
  });
}
